<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { fade, scale } from "svelte/transition";
  import { quintOut } from "svelte/easing";
  import { clickOutside } from "$actions/clickOutside";
  import { portal } from "$actions/portal.svelte";
  import CrossIconButton, {
    type CrossIconButtonVariant,
  } from "$components/+shared/CrossIconButton.svelte";

  export let open: boolean;
  export let maxWidth: string = "max-w-[566px]";
  export let maxHeight: string = "";
  export let disallowClickOutside: boolean = false;
  export let withCloseIcon: boolean = disallowClickOutside;
  export let closeIconVariant: CrossIconButtonVariant = "tertiary";
  export let fullScreen: boolean = false;
  export let radius: "rounded" | `rounded-${string}` = "rounded-xl";
  export let position: "top" | "center" | "bottom" = "center";
  // a universal faint backdrop for all modals or a heavier one for onboarding
  export let backdropVariant: "primary" | "secondary" | "tertiary" | "transparent" = "primary";

  const backdropVariants = {
    primary: "bg-gray-200/30 dark:bg-neutral-900/25",
    secondary: "bg-gray-200/70 backdrop-blur-lg dark:bg-neutral-900/70 dark:backdrop-blur-lg",
    tertiary: "bg-gray-200/75 dark:bg-neutral-900/75",
    transparent: "bg-transparent",
  };

  const positions = {
    top: "top-[14vh]",
    center: "top-1/2 -translate-y-1/2",
    bottom: "bottom-[14vh]",
  };

  const dispatch = createEventDispatcher();
</script>

{#if open}
  <div
    use:portal={{ target: "body", hasBackLayer: true }}
    class="contents"
    use:clickOutside={() => {
      if (!disallowClickOutside) {
        open = false;
        dispatch("close");
      }
    }}
  >
    <div
      class="absolute z-modal {positions[
        position
      ]} left-1/2 -translate-x-1/2 px-3 py-6 transition-[max-height] duration-[300ms] {fullScreen
        ? ''
        : `${maxHeight} ${maxWidth}`}"
      class:h-full={maxHeight && !fullScreen}
      class:w-full={maxWidth && !fullScreen}
      class:w-screen={fullScreen}
      class:h-dvh={fullScreen}
    >
      <div
        transition:fade={{ duration: 125, easing: quintOut }}
        class="fixed inset-0 {backdropVariants[backdropVariant]}"
        use:portal
      />
      <slot name="backdrop" />
      <div
        transition:scale={{ duration: 75, easing: quintOut, start: 0.9 }}
        class="{fullScreen
          ? ''
          : radius} glossy-effect scrollbar-thin flex size-full flex-col overflow-y-auto overflow-x-hidden border-[0.5px] border-black/10 [box-shadow:0_16px_60px_0_rgb(0_0_0/0.15)] dark:border-white/10 dark:[box-shadow:0_16px_50px_0_rgb(0_0_0/0.30)]"
      >
        <slot />
        {#if withCloseIcon}
          <CrossIconButton
            variant={closeIconVariant}
            on:click={() => {
              open = false;
              dispatch("close");
            }}
          />
        {/if}
      </div>
    </div>
  </div>
{/if}

<!-- close modal on Escape -->
<svelte:window
  on:keydown={(e) => {
    if (!open) return;
    if (e.key === "Escape" && !disallowClickOutside) {
      open = false;
      e.preventDefault();
      dispatch("close");
    }
  }}
/>
